// 🎣 Usage Limits Hook
// React hook for managing subscription limits and alerts

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  checkFeatureLimit,
  enforceFeatureLimit,
  getUnreadAlerts,
  markAlertAsRead,
  dismissAlert,
  getUsageSummary,
  canPerformOperation,
  ALERT_TYPES
} from '../utils/subscriptionLimits';

/**
 * 🎣 useUsageLimits Hook
 * Manages subscription limits and alerts for a company
 */
export const useUsageLimits = (companyId) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const alertCheckInterval = useRef(null);
  
  // Load alerts on mount and when company changes
  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    
    loadAlerts();
    loadSummary();
    
    // Check for new alerts every 30 seconds
    alertCheckInterval.current = setInterval(() => {
      loadAlerts();
    }, 30000);
    
    return () => {
      if (alertCheckInterval.current) {
        clearInterval(alertCheckInterval.current);
      }
    };
  }, [companyId]);
  
  /**
   * Load unread alerts
   */
  const loadAlerts = useCallback(async () => {
    if (!companyId) return;
    
    try {
      const unreadAlerts = await getUnreadAlerts(companyId);
      setAlerts(unreadAlerts);
      setLoading(false);
    } catch (error) {
      console.error('Error loading alerts:', error);
      setLoading(false);
    }
  }, [companyId]);
  
  /**
   * Load usage summary
   */
  const loadSummary = useCallback(async () => {
    if (!companyId) return;
    
    try {
      const data = await getUsageSummary(companyId);
      setSummary(data);
    } catch (error) {
      console.error('Error loading usage summary:', error);
    }
  }, [companyId]);
  
  /**
   * Check if operation is allowed
   */
  const checkLimit = useCallback(async (featureKey) => {
    if (!companyId) {
      return { allowed: true, unlimited: true };
    }
    
    try {
      return await checkFeatureLimit(companyId, featureKey);
    } catch (error) {
      console.error('Error checking limit:', error);
      return { allowed: true, error: error.message };
    }
  }, [companyId]);
  
  /**
   * Enforce limit before operation
   * Returns result or throws error if blocked
   */
  const enforceLimit = useCallback(async (featureKey, throwOnBlock = false) => {
    if (!companyId) {
      return { success: true, allowed: true };
    }
    
    try {
      const result = await enforceFeatureLimit(companyId, featureKey, throwOnBlock);
      
      // Reload alerts if new alert was created
      if (result.alertLevel) {
        loadAlerts();
      }
      
      return result;
    } catch (error) {
      // Reload alerts on error
      loadAlerts();
      throw error;
    }
  }, [companyId, loadAlerts]);
  
  /**
   * Quick check - just returns boolean
   */
  const canPerform = useCallback(async (featureKey) => {
    if (!companyId) return true;
    
    try {
      const result = await checkLimit(featureKey);
      return result.allowed;
    } catch (error) {
      console.error('Error checking if can perform:', error);
      return true; // Allow on error
    }
  }, [companyId, checkLimit]);
  
  /**
   * Dismiss an alert
   */
  const handleDismissAlert = useCallback(async (alertId) => {
    if (!companyId) return;
    
    try {
      await dismissAlert(companyId, alertId);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  }, [companyId]);
  
  /**
   * Mark alert as read
   */
  const handleMarkAsRead = useCallback(async (alertId) => {
    if (!companyId) return;
    
    try {
      await markAlertAsRead(companyId, alertId);
      setAlerts(prev => prev.map(a => 
        a.id === alertId ? { ...a, read: true } : a
      ));
    } catch (error) {
      console.error('Error marking alert as read:', error);
    }
  }, [companyId]);
  
  /**
   * Refresh all data
   */
  const refresh = useCallback(() => {
    loadAlerts();
    loadSummary();
  }, [loadAlerts, loadSummary]);
  
  // Get counts for different alert types
  const alertCounts = {
    total: alerts.length,
    blocked: alerts.filter(a => a.type === ALERT_TYPES.BLOCKED).length,
    critical: alerts.filter(a => a.type === ALERT_TYPES.CRITICAL).length,
    warning: alerts.filter(a => a.type === ALERT_TYPES.WARNING).length
  };
  
  // Check if any features are blocked
  const hasBlockedFeatures = alertCounts.blocked > 0;
  
  return {
    // Data
    alerts,
    summary,
    loading,
    alertCounts,
    hasBlockedFeatures,
    
    // Methods
    checkLimit,
    enforceLimit,
    canPerform,
    dismissAlert: handleDismissAlert,
    markAsRead: handleMarkAsRead,
    refresh
  };
};

/**
 * 🎣 useFeatureLimit Hook
 * Quick hook for checking a single feature limit
 */
export const useFeatureLimit = (companyId, featureKey) => {
  const [limitCheck, setLimitCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!companyId || !featureKey) {
      setLoading(false);
      return;
    }
    
    checkLimitStatus();
  }, [companyId, featureKey]);
  
  const checkLimitStatus = async () => {
    try {
      setLoading(true);
      const result = await checkFeatureLimit(companyId, featureKey);
      setLimitCheck(result);
      setLoading(false);
    } catch (error) {
      console.error('Error checking feature limit:', error);
      setLoading(false);
    }
  };
  
  return {
    limitCheck,
    loading,
    allowed: limitCheck?.allowed ?? true,
    unlimited: limitCheck?.unlimited ?? false,
    percentage: limitCheck?.percentage ?? 0,
    refresh: checkLimitStatus
  };
};

/**
 * 🎣 useOperationGuard Hook
 * Guards an operation with limit checking
 */
export const useOperationGuard = (companyId, featureKey) => {
  const [isChecking, setIsChecking] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [limitCheck, setLimitCheck] = useState(null);
  
  /**
   * Execute operation with limit guard
   * @param {Function} operation - The operation to execute
   * @param {Object} options - Options
   * @returns {Promise} - Operation result
   */
  const guardedExecute = useCallback(async (operation, options = {}) => {
    const { 
      throwOnBlock = false,
      showModal = true 
    } = options;
    
    try {
      setIsChecking(true);
      
      // Check limit
      const result = await enforceFeatureLimit(companyId, featureKey, throwOnBlock);
      
      setLimitCheck(result);
      
      if (!result.allowed) {
        setLimitReached(true);
        
        if (throwOnBlock) {
          const error = new Error(`Plan limit reached for ${result.featureName}`);
          error.code = 'PLAN_LIMIT_REACHED';
          error.limitCheck = result;
          throw error;
        }
        
        return {
          success: false,
          blocked: true,
          limitCheck: result
        };
      }
      
      // Execute the operation
      const operationResult = await operation();
      
      setIsChecking(false);
      return {
        success: true,
        blocked: false,
        result: operationResult,
        limitCheck: result
      };
      
    } catch (error) {
      setIsChecking(false);
      
      if (error.code === 'PLAN_LIMIT_REACHED') {
        setLimitReached(true);
        setLimitCheck(error.limitCheck);
      }
      
      throw error;
    }
  }, [companyId, featureKey]);
  
  /**
   * Reset limit reached state
   */
  const resetLimitReached = useCallback(() => {
    setLimitReached(false);
    setLimitCheck(null);
  }, []);
  
  return {
    guardedExecute,
    isChecking,
    limitReached,
    limitCheck,
    resetLimitReached
  };
};

export default useUsageLimits;

